import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectSelectedTask } from "../../../slice/taskSlice";
import { getTaskById, updateTask } from "../../../Api/taskApi";
import { fetchAllOrganizations } from "../../../slice/organizationsSlice";
import { fetchDepartments } from "../../../slice/departmentsSlice";
import { useToast } from "../../../components/Toast";
import { useThemeColor } from "../../../hooks/useThemeColor";
import { useTranslation } from "../../../i18n/LanguageContext";
import { Info, FileText, Target, ChevronDown, X, BrainCircuit, Shield, Lock, AlertTriangle, Eye, Building2 } from "lucide-react";
import axios from "axios";
import { BASE_URL } from "../../../components/constants";
import { authHeaders } from '../../../utils/authHeaders';

const DISCLAIMER_ICONS = [
  { key: 'shield', Icon: Shield },
  { key: 'lock', Icon: Lock },
  { key: 'info', Icon: Info },
  { key: 'warning', Icon: AlertTriangle },
  { key: 'eye', Icon: Eye },
  { key: 'building', Icon: Building2 },
];

// Theme-aware toggle switch — matches the pattern already used in
// ProjectSetupBuilder.jsx, so per-agent toggles look consistent everywhere.
const Toggle = ({ enabled, onToggle, aispaceColor, disabled }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onToggle}
    disabled={disabled}
    className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
    style={{ backgroundColor: enabled ? aispaceColor : '#D1D5DB' }}
  >
    <span
      className="inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
      style={{ transform: enabled ? 'translateX(20px)' : 'translateX(0)' }}
    />
  </button>
);

const Overview = () => {
  const dispatch = useDispatch();
  const { addToast, ToastContainer } = useToast();
  const { aispaceColor } = useThemeColor();
  const { t } = useTranslation();

  // Get selected task from Redux store
  const selectedTask = useSelector(selectSelectedTask);
  const { data } = useSelector((state) => state.admin);
  const { organizations, status: organizationsStatus, error: organizationsError } = useSelector((state) => state.organizations);
  const { departments } = useSelector((state) => state.departments);

  // State for form inputs
  const [taskName, setTaskName] = useState("");
  const [description, setDescription] = useState("");
  const [problem, setProblem] = useState("");
  const [solution, setSolution] = useState("");
  const [goal, setGoal] = useState("");
  const [kpis, setKpis] = useState([]);
  const [newKpi, setNewKpi] = useState("");
  const [manager, setManager] = useState("");
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [disclaimerEnabled, setDisclaimerEnabled] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState("");
  const [disclaimerLearnMoreContent, setDisclaimerLearnMoreContent] = useState("");
  const [disclaimerIcon, setDisclaimerIcon] = useState("shield");
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [pendingImageFile, setPendingImageFile] = useState(null); // file to upload on save
  const [imageRemoved, setImageRemoved] = useState(false); // marked for removal on save
  const [imageName, setImageName] = useState("");
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [deptDropdownOpen, setDeptDropdownOpen] = useState(false);
  const imageInputRef = useRef(null);
  const orgDropdownRef = useRef(null);
  const deptDropdownRef = useRef(null);

  // Close custom dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target)) setOrgDropdownOpen(false);
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(e.target)) setDeptDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAdmin = data?.user_role === "SUPER_ADMIN" || data?.user_role === "ADMIN";
  const isSuperAdmin = data?.user_role === "SUPER_ADMIN";
  const userOrganizationId = data?.organization_id;
  const canEdit = isAdmin && (isSuperAdmin || selectedTask?.organization_id === userOrganizationId);
  const selectedDepartmentName = departmentId
    ? departments.find(d => String(d.id) === String(departmentId))?.department_name
    : null;

  // Fetch organizations when component mounts — skip if already cached.
  // create/update/assign-subscription reset status back to 'idle' so this
  // still refetches after those; delete patches the list locally instead.
  useEffect(() => {
    if (organizationsStatus !== 'succeeded' && organizationsStatus !== 'loading') {
      dispatch(fetchAllOrganizations());
    }
  }, [dispatch, organizationsStatus]);

  // Fetch departments when organization changes
  useEffect(() => {
    if (selectedOrganization) {
      dispatch(fetchDepartments(selectedOrganization));
    }
  }, [dispatch, selectedOrganization]);

  // Initialize form with selected task data (already fetched by parent component)
  useEffect(() => {
    if (selectedTask) {
      const taskData = {
        name: selectedTask.name || "",
        description: selectedTask.description || "",
        problem: selectedTask.problem || "",
        solution: selectedTask.solution || "",
        goal: selectedTask.goal || "",
        kpi: selectedTask.kpi || "",
        manager: selectedTask.manager || "",
        organization_id: selectedTask.organization_id || "",
        department_id: selectedTask.department_id || "",
        disclaimer_enabled: selectedTask.disclaimer_enabled != null ? Boolean(selectedTask.disclaimer_enabled) : false,
        disclaimer_text: selectedTask.disclaimer_text || "",
        disclaimer_learn_more_content: selectedTask.disclaimer_learn_more_content || "",
        disclaimer_icon: selectedTask.disclaimer_icon || "shield"
      };

      setTaskName(taskData.name);
      setDescription(taskData.description);
      setProblem(taskData.problem);
      setSolution(taskData.solution);
      setGoal(taskData.goal);
      setKpis(taskData.kpi ? taskData.kpi.split(",").map(k => k.trim()).filter(Boolean) : []);
      setManager(taskData.manager);
      setSelectedOrganization(taskData.organization_id);
      setDepartmentId(taskData.department_id);
      setDisclaimerEnabled(taskData.disclaimer_enabled);
      setDisclaimerText(taskData.disclaimer_text);
      setDisclaimerLearnMoreContent(taskData.disclaimer_learn_more_content);
      setDisclaimerIcon(taskData.disclaimer_icon);
      setInitialData(taskData);
      setHasChanges(false);

      // Set image preview if agent has an image
      if (selectedTask.image_blob_path) {
        setImagePreview(`${BASE_URL}/agents/media/${selectedTask.id}`);
      } else {
        setImagePreview(null);
      }
      setPendingImageFile(null);
      setImageRemoved(false);
      setImageName("");
      setIsLoading(false);
    }
  }, [selectedTask?.id]);

  // Check for changes
  useEffect(() => {
    if (initialData) {
      const currentKpisString = kpis.join(", ");
      const changed =
        taskName !== initialData.name ||
        description !== initialData.description ||
        problem !== initialData.problem ||
        solution !== initialData.solution ||
        goal !== initialData.goal ||
        currentKpisString !== initialData.kpi ||
        manager !== initialData.manager ||
        selectedOrganization !== initialData.organization_id ||
        departmentId !== initialData.department_id ||
        disclaimerEnabled !== initialData.disclaimer_enabled ||
        disclaimerText !== initialData.disclaimer_text ||
        disclaimerLearnMoreContent !== initialData.disclaimer_learn_more_content ||
        disclaimerIcon !== initialData.disclaimer_icon ||
        pendingImageFile !== null ||
        imageRemoved;
      setHasChanges(changed);
    }
  }, [taskName, description, problem, solution, goal, kpis, manager, selectedOrganization, departmentId, disclaimerEnabled, disclaimerText, disclaimerLearnMoreContent, disclaimerIcon, initialData, pendingImageFile, imageRemoved]);

  // Handle save
  const handleSave = async () => {
    if (!selectedTask) {
      addToast(t('agentSettings.toastNoTask'), "error");
      return;
    }

    if (!canEdit) {
      addToast(t('agentSettings.toastNoPermission'), "error");
      return;
    }

    setIsLoading(true);

    try {
      // Upload new image if pending
      if (pendingImageFile) {
        const formData = new FormData();
        formData.append('file', pendingImageFile);
        formData.append('task_id', String(selectedTask.id));
        await axios.post(`${BASE_URL}/agents/upload-image`, formData, authHeaders());
      }

      // Remove image if marked
      if (imageRemoved) {
        await axios.post(`${BASE_URL}/agents/remove-image`, { task_id: String(selectedTask.id) }, authHeaders());
      }

      // Save all other fields
      const taskData = {
        id: String(selectedTask.id),
        name: taskName,
        description,
        problem,
        solution,
        goal,
        kpi: kpis.join(", "),
        manager,
        organization_id: selectedOrganization || null,
        department_id: departmentId || null,
        disclaimer_enabled: disclaimerEnabled,
        disclaimer_text: disclaimerText,
        disclaimer_learn_more_content: disclaimerLearnMoreContent,
        disclaimer_icon: disclaimerIcon
      };

      await updateTask(taskData);

      const updatedTask = await getTaskById(selectedTask.id);
      const newInitialData = {
        name: updatedTask.name || "",
        description: updatedTask.description || "",
        problem: updatedTask.problem || "",
        solution: updatedTask.solution || "",
        goal: updatedTask.goal || "",
        kpi: updatedTask.kpi || "",
        manager: updatedTask.manager || "",
        organization_id: updatedTask.organization_id || "",
        department_id: updatedTask.department_id || "",
        disclaimer_enabled: updatedTask.disclaimer_enabled != null ? Boolean(updatedTask.disclaimer_enabled) : false,
        disclaimer_text: updatedTask.disclaimer_text || "",
        disclaimer_learn_more_content: updatedTask.disclaimer_learn_more_content || "",
        disclaimer_icon: updatedTask.disclaimer_icon || "shield"
      };
      setDisclaimerEnabled(newInitialData.disclaimer_enabled);
      setDisclaimerText(newInitialData.disclaimer_text);
      setDisclaimerLearnMoreContent(newInitialData.disclaimer_learn_more_content);
      setDisclaimerIcon(newInitialData.disclaimer_icon);
      setInitialData(newInitialData);
      setHasChanges(false);
      setPendingImageFile(null);
      setImageRemoved(false);
      setImageName("");

      // Update image preview from server
      if (updatedTask.image_blob_path) {
        setImagePreview(`${BASE_URL}/agents/media/${selectedTask.id}?t=${Date.now()}`);
      } else {
        setImagePreview(null);
      }

      addToast(t('agentSettings.toastUpdateSuccess'), "success");
    } catch (error) {
      addToast(error.message || t('agentSettings.toastUpdateError'), "error");
      console.error("Update task error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (initialData) {
      setTaskName(initialData.name);
      setDescription(initialData.description);
      setProblem(initialData.problem);
      setSolution(initialData.solution);
      setGoal(initialData.goal);
      setKpis(initialData.kpi ? initialData.kpi.split(",").map(k => k.trim()).filter(Boolean) : []);
      setManager(initialData.manager);
      setSelectedOrganization(initialData.organization_id);
      setDepartmentId(initialData.department_id);
      setDisclaimerEnabled(initialData.disclaimer_enabled);
      setDisclaimerText(initialData.disclaimer_text);
      setDisclaimerLearnMoreContent(initialData.disclaimer_learn_more_content);
      setDisclaimerIcon(initialData.disclaimer_icon);
      setPendingImageFile(null);
      setImageRemoved(false);
      setImageName("");
      // Restore original image preview
      if (selectedTask?.image_blob_path) {
        setImagePreview(`${BASE_URL}/agents/media/${selectedTask.id}`);
      } else {
        setImagePreview(null);
      }
      setHasChanges(false);
    }
  };

  // Handle image select — only local preview, actual upload happens on Save
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    setPendingImageFile(file);
    setImageRemoved(false);
    setImageName(file.name);

    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  // Handle image remove — only marks for removal, actual delete happens on Save
  const handleImageRemove = () => {
    setImagePreview(null);
    setPendingImageFile(null);
    setImageRemoved(true);
    setImageName("");
  };

  // Add KPI
  const handleAddKpi = () => {
    if (newKpi.trim() && !kpis.includes(newKpi.trim())) {
      setKpis([...kpis, newKpi.trim()]);
      setNewKpi("");
    }
  };

  // Remove KPI
  const handleRemoveKpi = (kpiToRemove) => {
    setKpis(kpis.filter(k => k !== kpiToRemove));
  };

  return (
    <div>
      <ToastContainer />

      {/* Header with Save/Cancel */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-[20px] font-semibold text-[#111827]">{t('agentSettings.generalTitle')}</h2>
          <p className="text-[14px] text-[#6B7280] mt-1">
            {t('agentSettings.generalSubtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-[14px] text-[#6B7280] ${hasChanges ? '' : 'invisible'}`}>{t('agentSettings.unsavedChanges')}</span>
          <button
            onClick={handleCancel}
            disabled={!hasChanges || isLoading}
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
          >
            {t('agentSettings.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || isLoading}
            className="px-5 py-2.5 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            style={{ backgroundColor: aispaceColor }}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('agentSettings.saving')}
              </>
            ) : (
              t('agentSettings.save')
            )}
          </button>
        </div>
      </div>

      {/* Basic Information Card */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] mb-6">
        {/* Card Header */}
        <div className="flex items-start gap-4 p-6">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${aispaceColor}20` }}
          >
            <Info size={20} style={{ color: aispaceColor }} />
          </div>
          <div>
            <h3 className="text-[16px] font-semibold text-[#111827]">{t('agentSettings.basicInfoTitle')}</h3>
            <p className="text-[14px] text-[#6B7280] mt-0.5">
              {t('agentSettings.basicInfoSubtitle')}
            </p>
          </div>
        </div>
        {/* Full-width border */}
        <div className="border-b border-[#E5E7EB]"></div>

        {/* Form Fields */}
        <div className="space-y-5 py-6 pl-[100px] pr-[100px]">
          {/* Agent Image */}
          <div>
            <label className="block text-[14px] font-medium text-[#374151] mb-3">{t('agentSettings.agentImageLabel')}</label>
            <div className="flex items-center gap-4">
              {/* Image Preview */}
              <div
                className="w-[48px] h-[48px] rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{ backgroundColor: aispaceColor }}
              >
                <img
                  src={imagePreview || 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'}
                  alt="Agent"
                  className={`w-full h-full object-cover ${imagePreview ? '' : 'hidden'}`}
                  onError={() => setImagePreview(null)}
                />
                <BrainCircuit className={`w-6 h-6 text-white ${imagePreview ? 'hidden' : ''}`} strokeWidth={2} />
              </div>

              {/* Hidden file input */}
              <input
                type="file"
                ref={imageInputRef}
                onChange={handleImageSelect}
                accept="image/*"
                className="hidden"
              />

              {/* Edit Image Button */}
              <button
                onClick={() => imageInputRef.current?.click()}
                disabled={!canEdit}
                className="px-4 py-2 text-[13px] font-medium text-white rounded-lg transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: aispaceColor }}
              >
                {t('agentSettings.editImage')}
              </button>

              {/* Remove Image Button - always in DOM, hidden via className */}
              <button
                onClick={handleImageRemove}
                disabled={!canEdit}
                className={`px-4 py-2 text-[13px] font-medium text-white bg-[#EF4444] rounded-lg transition-colors hover:bg-[#DC2626] disabled:opacity-50 ${imagePreview ? '' : 'hidden'}`}
              >
                {t('agentSettings.removeImage')}
              </button>

              {/* File name */}
              <span className="text-[13px] text-[#6B7280]">
                {imageName || (imagePreview ? '' : t('agentSettings.noFileSelected'))}
              </span>
            </div>
          </div>

          {/* Agent Name */}
          <div>
            <label className="block text-[14px] font-medium text-[#374151] mb-2">{t('agentSettings.agentNameLabel')}</label>
            <input
              type="text"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              placeholder={t('agentSettings.agentNamePlaceholder')}
              disabled={!canEdit}
              className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
              style={{ '--focus-color': aispaceColor }}
            />
          </div>

          {/* Organisation & Department Row */}
          {organizationsError && (
            <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2">{organizationsError}</div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">{t('agentSettings.organizationLabel')}</label>
              <div className="relative" ref={orgDropdownRef}>
                <button
                  type="button"
                  onClick={() => { if (isSuperAdmin) { setOrgDropdownOpen(!orgDropdownOpen); setDeptDropdownOpen(false); } }}
                  disabled={!isSuperAdmin}
                  className="w-full px-4 py-3 pr-10 text-[14px] text-left border rounded-lg bg-white focus:outline-none cursor-pointer disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                  style={{ borderColor: orgDropdownOpen ? aispaceColor : '#E5E7EB' }}
                >
                  <span className={selectedOrganization ? 'text-[#111827]' : 'text-[#9CA3AF]'}>
                    {selectedOrganization
                      ? organizations.find(o => o.organization_id === selectedOrganization)?.organization_name
                      : t('agentSettings.selectOrganization')}
                  </span>
                </button>
                <ChevronDown size={18} className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6B7280] transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
                {orgDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-[200px] overflow-y-auto">
                    <div
                      onClick={() => { setSelectedOrganization(''); setOrgDropdownOpen(false); }}
                      className="px-4 py-2.5 text-[14px] text-[#9CA3AF] hover:bg-[#F9FAFB] cursor-pointer"
                    >
                      {t('agentSettings.selectOrganization')}
                    </div>
                    {organizations.map((org) => (
                      <div
                        key={org.organization_id}
                        onClick={() => { if (org.organization_id !== selectedOrganization) setDepartmentId(''); setSelectedOrganization(org.organization_id); setOrgDropdownOpen(false); }}
                        className={`px-4 py-2.5 text-[14px] cursor-pointer transition-colors ${
                          selectedOrganization === org.organization_id ? 'font-medium' : 'text-[#374151] hover:bg-[#F9FAFB]'
                        }`}
                        style={selectedOrganization === org.organization_id ? { backgroundColor: `${aispaceColor}10`, color: aispaceColor } : {}}
                      >
                        {org.organization_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">{t('agentSettings.departmentLabel')}</label>
              <div className="relative" ref={deptDropdownRef}>
                <button
                  type="button"
                  onClick={() => { if (canEdit && selectedOrganization) { setDeptDropdownOpen(!deptDropdownOpen); setOrgDropdownOpen(false); } }}
                  disabled={!canEdit || !selectedOrganization}
                  className="w-full px-4 py-3 pr-10 text-[14px] text-left border rounded-lg bg-white focus:outline-none cursor-pointer disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                  style={{ borderColor: deptDropdownOpen ? aispaceColor : '#E5E7EB' }}
                >
                  <span className={selectedDepartmentName ? 'text-[#111827]' : 'text-[#9CA3AF]'}>
                    {selectedDepartmentName || t('agentSettings.selectDepartment')}
                  </span>
                </button>
                <ChevronDown size={18} className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6B7280] transition-transform ${deptDropdownOpen ? 'rotate-180' : ''}`} />
                {deptDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-[200px] overflow-y-auto">
                    <div
                      onClick={() => { setDepartmentId(''); setDeptDropdownOpen(false); }}
                      className="px-4 py-2.5 text-[14px] text-[#9CA3AF] hover:bg-[#F9FAFB] cursor-pointer"
                    >
                      {t('agentSettings.selectDepartment')}
                    </div>
                    {departments.map((dept) => (
                      <div
                        key={dept.id}
                        onClick={() => { setDepartmentId(String(dept.id)); setDeptDropdownOpen(false); }}
                        className={`px-4 py-2.5 text-[14px] cursor-pointer transition-colors ${
                          String(departmentId) === String(dept.id) ? 'font-medium' : 'text-[#374151] hover:bg-[#F9FAFB]'
                        }`}
                        style={String(departmentId) === String(dept.id) ? { backgroundColor: `${aispaceColor}10`, color: aispaceColor } : {}}
                      >
                        {dept.department_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-[14px] font-medium text-[#374151] mb-2">{t('agentSettings.descriptionLabel')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('agentSettings.descriptionPlaceholder')}
              disabled={!canEdit}
              rows={4}
              className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors resize-none disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
              style={{ '--focus-color': aispaceColor }}
            />
            <p className="text-[13px] text-[#9CA3AF] mt-2">
              {t('agentSettings.descriptionHelp')}
            </p>
          </div>
        </div>
      </div>

      {/* Use Case Card */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] mb-6">
        {/* Card Header */}
        <div className="flex items-start gap-4 p-6">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${aispaceColor}20` }}
          >
            <FileText size={20} style={{ color: aispaceColor }} />
          </div>
          <div>
            <h3 className="text-[16px] font-semibold text-[#111827]">{t('agentSettings.useCaseTitle')}</h3>
            <p className="text-[14px] text-[#6B7280] mt-0.5">
              {t('agentSettings.useCaseSubtitle')}
            </p>
          </div>
        </div>
        {/* Full-width border */}
        <div className="border-b border-[#E5E7EB]"></div>

        {/* Form Fields */}
        <div className="space-y-5 py-6 pl-[100px] pr-[100px]">
          {/* Problem Description */}
          <div>
            <label className="block text-[14px] font-medium text-[#374151] mb-2">{t('agentSettings.problemLabel')}</label>
            <textarea
              value={problem}
              onChange={(e) => setProblem(e.target.value)}
              placeholder={t('agentSettings.problemPlaceholder')}
              disabled={!canEdit}
              rows={4}
              className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors resize-none disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
              style={{ '--focus-color': aispaceColor }}
            />
            <p className="text-[13px] text-[#9CA3AF] mt-2">
              {t('agentSettings.problemHelp')}
            </p>
          </div>

          {/* Solution Description */}
          <div>
            <label className="block text-[14px] font-medium text-[#374151] mb-2">{t('agentSettings.solutionLabel')}</label>
            <textarea
              value={solution}
              onChange={(e) => setSolution(e.target.value)}
              placeholder={t('agentSettings.solutionPlaceholder')}
              disabled={!canEdit}
              rows={4}
              className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors resize-none disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
              style={{ '--focus-color': aispaceColor }}
            />
            <p className="text-[13px] text-[#9CA3AF] mt-2">
              {t('agentSettings.solutionHelp')}
            </p>
          </div>
        </div>
      </div>

      {/* Goals & KPIs Card */}
      <div className="bg-white rounded-xl border border-[#E5E7EB]">
        {/* Card Header */}
        <div className="flex items-start gap-4 p-6">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${aispaceColor}20` }}
          >
            <Target size={20} style={{ color: aispaceColor }} />
          </div>
          <div>
            <h3 className="text-[16px] font-semibold text-[#111827]">{t('agentSettings.goalsTitle')}</h3>
            <p className="text-[14px] text-[#6B7280] mt-0.5">
              {t('agentSettings.goalsSubtitle')}
            </p>
          </div>
        </div>
        {/* Full-width border */}
        <div className="border-b border-[#E5E7EB]"></div>

        {/* Form Fields */}
        <div className="space-y-5 py-6 pl-[100px] pr-[100px]">
          {/* Goal */}
          <div>
            <label className="block text-[14px] font-medium text-[#374151] mb-2">{t('agentSettings.goalLabel')}</label>
            <input
              type="text"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder={t('agentSettings.goalPlaceholder')}
              disabled={!canEdit}
              className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
              style={{ '--focus-color': aispaceColor }}
            />
            <p className="text-[13px] text-[#9CA3AF] mt-2">
              {t('agentSettings.goalHelp')}
            </p>
          </div>

          {/* KPIs */}
          <div>
            <label className="block text-[14px] font-medium text-[#374151] mb-2">{t('agentSettings.kpisLabel')}</label>

            {/* KPI Tags */}
            {kpis.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {kpis.map((kpi, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-full text-white"
                    style={{ backgroundColor: aispaceColor }}
                  >
                    {kpi}
                    {canEdit && (
                      <button
                        onClick={() => handleRemoveKpi(kpi)}
                        className="hover:opacity-80"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}

            {/* Add KPI Input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newKpi}
                onChange={(e) => setNewKpi(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddKpi()}
                placeholder={t('agentSettings.kpiPlaceholder')}
                disabled={!canEdit}
                className="flex-1 px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                style={{ '--focus-color': aispaceColor }}
              />
              <button
                onClick={handleAddKpi}
                disabled={!canEdit || !newKpi.trim()}
                className="px-5 py-3 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: aispaceColor }}
              >
                {t('agentSettings.kpiAdd')}
              </button>
            </div>
            <p className="text-[13px] text-[#9CA3AF] mt-2">
              {t('agentSettings.kpiHelp')}
            </p>
          </div>

          {/* Process Manager */}
          <div>
            <label className="block text-[14px] font-medium text-[#374151] mb-2">{t('agentSettings.managerLabel')}</label>
            <div className="relative">
              <input
                type="text"
                value={manager}
                onChange={(e) => setManager(e.target.value)}
                placeholder={t('agentSettings.managerPlaceholder')}
                disabled={!canEdit}
                className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                style={{ '--focus-color': aispaceColor }}
              />
            </div>
            <p className="text-[13px] text-[#9CA3AF] mt-2">
              {t('agentSettings.managerHelp')}
            </p>
          </div>
        </div>
      </div>

      {/* Privacy Disclaimer Card — one shared config per agent, valid across
          every Knowledge Source mode (documents/web/hybrid), not per-mode. */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] mt-6">
        <div className="flex items-start justify-between gap-4 p-6">
          <div className="flex items-start gap-4">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${aispaceColor}20` }}
            >
              <Shield size={20} style={{ color: aispaceColor }} />
            </div>
            <div>
              <h3 className="text-[16px] font-semibold text-[#111827]">{t('agentSettings.disclaimerToggleLabel')}</h3>
              <p className="text-[14px] text-[#6B7280] mt-0.5">
                {t('agentSettings.disclaimerToggleDescription')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[14px] font-medium" style={{ color: disclaimerEnabled ? aispaceColor : '#6B7280' }}>
              {disclaimerEnabled ? t('agentSettings.toggleOn') : t('agentSettings.toggleOff')}
            </span>
            <Toggle
              enabled={disclaimerEnabled}
              onToggle={() => setDisclaimerEnabled(!disclaimerEnabled)}
              aispaceColor={aispaceColor}
              disabled={!canEdit}
            />
          </div>
        </div>

        {disclaimerEnabled && (
          <>
            <div className="border-b border-[#E5E7EB]"></div>
            <div className="space-y-5 py-6 pl-[100px] pr-[100px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[14px] font-medium text-[#374151] mb-2">
                    {t('agentSettings.disclaimerTextLabel')}
                  </label>
                  <textarea
                    value={disclaimerText}
                    onChange={(e) => setDisclaimerText(e.target.value)}
                    disabled={!canEdit}
                    rows={4}
                    className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors resize-none disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                    style={{ '--focus-color': aispaceColor }}
                  />
                  <p className="text-[13px] text-[#9CA3AF] mt-2">
                    {t('agentSettings.disclaimerTextHelp')}
                  </p>
                </div>
                <div>
                  <label className="block text-[14px] font-medium text-[#374151] mb-2">
                    {t('agentSettings.disclaimerLearnMoreContentLabel')}
                  </label>
                  <textarea
                    value={disclaimerLearnMoreContent}
                    onChange={(e) => setDisclaimerLearnMoreContent(e.target.value)}
                    disabled={!canEdit}
                    rows={4}
                    className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors resize-none disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                    style={{ '--focus-color': aispaceColor }}
                  />
                  <p className="text-[13px] text-[#9CA3AF] mt-2">
                    {t('agentSettings.disclaimerLearnMoreContentHelp')}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-[14px] font-medium text-[#374151] mb-2">
                  {t('agentSettings.disclaimerIconLabel')}
                </label>
                <div className="flex gap-2">
                  {DISCLAIMER_ICONS.map(({ key, Icon }) => (
                    <button
                      key={key}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => setDisclaimerIcon(key)}
                      className="w-11 h-11 rounded-lg border-2 flex items-center justify-center transition-colors disabled:cursor-not-allowed"
                      style={{
                        borderColor: disclaimerIcon === key ? aispaceColor : '#E5E7EB',
                        color: disclaimerIcon === key ? aispaceColor : '#9CA3AF',
                      }}
                    >
                      <Icon size={18} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F9FAFB] text-[13px] text-[#6B7280]">
                <Info size={16} className="flex-shrink-0 mt-0.5 text-[#9CA3AF]" />
                <span>{t('agentSettings.disclaimerInfoNotice')}</span>
              </div>

              <div>
                <label className="block text-[14px] font-medium text-[#374151] mb-2">
                  {t('agentSettings.disclaimerPreviewLabel')}
                </label>
                <div className="rounded-2xl border border-[#E5E7EB] bg-[#FAFAFA] p-4">
                  <div className="max-w-xl">
                    {(() => {
                      const ActiveIcon = DISCLAIMER_ICONS.find((i) => i.key === disclaimerIcon)?.Icon || Shield;
                      return (
                        <div className="mx-[25px] -mb-3">
                          <div
                            className="flex items-center gap-2 px-4 py-3.5 rounded-2xl"
                            style={{ backgroundColor: `${aispaceColor}0D` }}
                          >
                            <ActiveIcon size={13} style={{ color: aispaceColor }} className="flex-shrink-0" />
                            <span className="text-[11.5px] text-[#374151] flex-1 truncate">
                              {disclaimerText || t('agentSettings.disclaimerTextLabel')}
                            </span>
                            <span className="text-[11.5px] font-medium underline flex-shrink-0" style={{ color: aispaceColor }}>
                              {t('agentSettings.disclaimerLearnMoreLink')}
                            </span>
                            <X size={12} className="text-[#9CA3AF] flex-shrink-0" />
                          </div>
                        </div>
                      );
                    })()}
                    <div className="bg-white border border-[#E5E7EB] rounded-full px-4 py-3 text-[13px] text-[#9CA3AF]">
                      {t('chatActions.chatPlaceholder')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Overview;
